import React from 'react';
import { Link } from '@inertiajs/react';
import { Card, Statistic, DataTable } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { quoted as Q } from '~/marketing/marketingData';
import { Post, H2, body, note, BENCHMARK, GraderCta } from '~/marketing/Post';
import { benchmark, I, by, span, listingShare, ownShare, totals, ranges, count, one, word, range, and } from '~/marketing/research/figures';

/* The local services benchmark, the site's first research page.

   **Every figure comes from `benchmark.json`**, which `bin/rails
   research:benchmark` writes from the corpus — nothing here is typed. The
   prose names industries editorially ("roofing least"), and
   `benchmark_post_test` holds each of those claims against the file, so a
   re-run that changes the order fails a test rather than leaving a sentence
   false. Class 2 strings come from `quoted` verbatim.

   Published only for the ten industries measured on the three benchmark
   engines; med spa, PI law and dental pool a fourth and are not comparable. */

// By brand, the measure the section leads with; `top3_median` is shown beside
// it as written. See `Scorecard#brand_rows`.
const cityTop3 = i => span(i.cities.map(c => c.top3_by_brand));
const cityCount = i => span(i.cities.map(c => c.brands));

const { answers, named, asked } = totals;
const [namedLow] = ranges.named;
const [perLow, perHigh] = ranges.perAnswer;
const [overlapLow, overlapHigh] = ranges.overlap;
const [listLow, listHigh] = ranges.listings;
const [ownLow, ownHigh] = ranges.own;
const dates = [...new Set(I.map(i => i.measured_on))];

const sec = { display: 'grid', gap: 'var(--space-5)' };
/* A table cell figure, right-aligned to sit under its right-aligned header.
   `Statistic` is a flex column that fills the cell, so `text-align` never
   reaches the figure; the design system is not edited, so the cell says it. */
const Num = props => <Statistic size="sm" style={{ alignItems: 'flex-end' }} {...props} />;
const industry = { key: 'industry', header: 'Industry', render: i => i.label };
const measured = { key: 'measured', header: 'Measured', render: i => <span className="sp-mono">{i.measured_on}</span> };

export default function LocalServicesBenchmark() {
  return (
    <Post path={BENCHMARK} gap="var(--space-16)" lede={<>
      We asked {and(benchmark.engines)} the questions a customer asks before hiring someone local: twenty per industry, in {and(benchmark.metros.map(m => m.split(',')[0]))}, {word(benchmark.samples)} times each. Ten industries and {count(answers)} answers. Here is who they named, how much that changed each time we asked, and which sources they cited.
    </>}>
      <section style={sec}>
        <H2>Asked who to hire, they name a business</H2>
        <Card variant="data">
          <Statistic label="Who-to-hire answers that named a specific business" value={named} of={asked} noun="answers" size="lg" />
        </Card>
        <p style={body}>
          In every industry, at least {Math.floor(namedLow)} in 100 answers to a who-to-hire question named a specific business. Family law was lowest, at {by.family_law.named.count} of {by.family_law.named.of}; the rest of its answers gave general advice or pointed to a directory. On average, an answer named between {one(perLow)} and {one(perHigh)} businesses, depending on the industry, and that is the shortlist a customer reads.
        </p>
        <DataTable hover={false} rows={I.map(i => ({ ...i, id: i.key }))} columns={[
          industry,
          { key: 'named', header: 'Named a business', numeric: true,
            render: i => <Num value={i.named.count} of={i.named.of} noun="answers" /> },
          { key: 'per', header: 'Businesses per answer', numeric: true,
            render: i => <Num value={one(i.named_per_answer)} /> },
          measured,
        ]} />
      </section>

      <section style={sec}>
        <H2>Ask again, and the list changes</H2>
        <p style={body}>
          Every question was asked {word(benchmark.samples)} times. Of all the businesses named in two answers to the same question, {range([overlapLow, overlapHigh], '%')} appeared in both. In-home senior care changed most, at {by.home_care.repeat_overlap}%, and roofing least, at {by.roofing.repeat_overlap}%.
        </p>
        <p style={body}>
          So one answer on its own can mislead. Being named across repeated answers is the reading worth acting on, and every figure on this page is counted that way.
        </p>
        <DataTable hover={false} rows={I.map(i => ({ ...i, id: i.key }))} columns={[
          industry,
          { key: 'overlap', header: 'Named in both answers', numeric: true,
            render: i => <Num value={i.repeat_overlap} unit="%" /> },
          measured,
        ]} />
      </section>

      <section style={sec}>
        <H2>Some markets are spread wide, and some are concentrated</H2>
        <p style={body}>
          In each city we counted every time a business was named, with a franchise’s location names counted as its brand, and the share that went to the three named most. In landscaping and accounting, the top three took {range(span([cityTop3(by.landscaping), cityTop3(by.accounting)].flat().map(Math.round)), '%')} in every city, spread across {range(span([cityCount(by.landscaping), cityCount(by.accounting)].flat()))} businesses. In roofing they took {range(cityTop3(by.roofing).map(Math.round), '%')} in every city, across {range(cityCount(by.roofing))}.
        </p>
        <p style={body}>
          In-home senior care changes most with the counting. The answers name one franchise in many ways, with and without its city. Counted as written, its top three took {one(by.home_care.top3_median)}% in the middle of the three cities; counted by brand, {one(by.home_care.top3_by_brand_median)}%.
        </p>
        <p style={body}>
          Who leads changes from city to city. In pest control, the two most-named companies in Dallas were national chains. In Nashville the same chains came third and fourth, and in Boise neither was in the top four. For in-home senior care, the most-named business in every city was a franchise brand.
        </p>
        <DataTable hover={false} rows={I.map(i => ({ ...i, id: i.key }))} columns={[
          industry,
          { key: 'top3', header: 'Top three’s share, by brand', numeric: true,
            render: i => <Num value={one(i.top3_by_brand_median)} unit="%" /> },
          { key: 'written', header: 'As written', numeric: true,
            render: i => <Num value={one(i.top3_median)} unit="%" /> },
          { key: 'cities', header: 'Businesses named per city', numeric: true,
            render: i => <span className="sp-mono">{range(cityCount(i))}</span> },
          measured,
        ]} />
        <p style={note}>Each share is the middle of the three cities’ figures.</p>
        <p style={note}>{Q.benchmark_franchise_names}</p>
      </section>

      <section style={sec}>
        <H2>Where the answers come from</H2>
        <p style={body}>
          The answers cite their sources. Across the ten industries, {range([one(listLow), one(listHigh)], '%')} of citations went to places a business can be listed, reviewed or mentioned (directories, review sites, forums, roundups, news and reference sites), and {range([one(ownLow), one(ownHigh)], '%')} to businesses’ own websites.
        </p>
        <p style={body}>
          So there are two kinds of work. One is being listed where AI assistants look. The other is a page of your own that answers the question, because in many of these answers a competitor’s page already does. Family law leans most on listings, at {one(listingShare(by.family_law))}%, and chiropractic least, at {one(listingShare(by.chiropractic))}%.
        </p>
        <DataTable hover={false} rows={I.map(i => ({ ...i, id: i.key }))} columns={[
          industry,
          { key: 'listings', header: 'Cited places to be listed', numeric: true,
            render: i => <Num value={i.citations.listings} of={i.citations.of} noun="citations" /> },
          { key: 'own', header: 'Cited businesses’ own sites', numeric: true,
            render: i => <Num value={one(ownShare(i))} unit="%" /> },
          measured,
        ]} />
      </section>

      <section style={sec}>
        <H2>How this was measured</H2>
        <p style={body}>{Q.benchmark_scope}</p>
        <p style={body}>{Q.measurement_basis}</p>
        <p style={body}>
          Each industry was measured once, on the date beside it: {dates.join(' and ')}. How each answer becomes a number is on the <Link href={routes.methodology}>methodology page</Link>.
        </p>
      </section>

      <GraderCta heading="See who AI assistants name in your city." />
    </Post>
  );
}

LocalServicesBenchmark.layout = withMarketingLayout;
